import React, { useState } from 'react';
import { Database } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { submitCDERequest } from '../../services/cmsService';

const CDERequestButton = ({ auctionId, variant = 'button' }) => {
  const { isAuthenticated, loading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const handleButtonClick = async () => {
    if (loading) return;
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    setIsLoading(true);
    setNotification(null);

    try {
      const result = await submitCDERequest(auctionId);

      if (result.success) {
        setNotification({
          type: 'success',
          message: result.message,
          details: result.requestId
            ? `ID Cerere: ${result.requestId}\nLivrare estimata: ${result.estimatedDelivery || '48 ore'}`
            : null,
        });
      } else {
        setNotification({
          type: 'error',
          message: result.message || 'A aparut o eroare la trimiterea cererii.',
        });
      }
    } catch (error) {
      console.error('Eroare la trimiterea cererii CDE:', error);
      setNotification({
        type: 'error',
        message: 'A aparut o eroare. Va rugam sa incercati din nou.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const authModal = showAuthModal && (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => {
        e.stopPropagation();
        setShowAuthModal(false);
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowAuthModal(false);
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold"
          aria-label="Inchide dialogul"
        >
          x
        </button>

        <div className="text-center">
          <div className="mb-4">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Database size={32} className="text-teal-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              Autentificare necesara
            </h3>
            <p className="text-gray-600 mb-6">
              Pentru a solicita CDE (Common Data Environment), trebuie sa te
              autentifici in cont.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <a
              href="/login"
              onClick={(e) => e.stopPropagation()}
              className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl text-center"
            >
              Autentificare
            </a>
            <a
              href="/register"
              onClick={(e) => e.stopPropagation()}
              className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl text-center"
            >
              Creaza cont nou
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  const notificationBlock = notification && (
    <div
      className={`mt-4 p-4 rounded-xl border-l-4 ${
        notification.type === 'success'
          ? 'bg-green-50 border-green-400 text-green-800'
          : 'bg-red-50 border-red-400 text-red-800'
      } shadow-md relative`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center ${
                notification.type === 'success' ? 'bg-green-400' : 'bg-red-400'
              }`}
            >
              <span className="text-white text-sm font-bold">
                {notification.type === 'success' ? '\u2713' : '!'}
              </span>
            </div>
            <p className="font-semibold text-base">{notification.message}</p>
          </div>

          {notification.details && (
            <div className="ml-9">
              <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                <pre className="text-sm whitespace-pre-line font-mono text-gray-700 leading-relaxed">
                  {notification.details}
                </pre>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => setNotification(null)}
          className="text-gray-400 hover:text-gray-600 ml-4 text-xl font-bold p-1 rounded-full hover:bg-white/50 transition-colors"
          aria-label="Inchide notificarea"
        >
          x
        </button>
      </div>
    </div>
  );

  if (variant === 'section') {
    return (
      <>
        <section className="bg-gradient-to-br from-teal-50 via-white to-emerald-50 border border-teal-200 rounded-xl shadow-lg p-4 flex flex-col justify-between gap-3 h-full">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Database size={20} className="text-teal-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-teal-700 leading-tight">
                  Vreau CDE
                </h2>
                <span className="text-[10px] text-teal-400 font-medium">
                  ISO SR 19650
                </span>
              </div>
            </div>
            <p className="text-gray-600 text-xs leading-snug mb-2">
              Solicita un Common Data Environment pentru gestionarea
              informatiilor conform standardului ISO SR 19650.
            </p>
            <ul className="space-y-1 mb-3">
              {[
                'Mediu colaborativ digital',
                'Gestionare documente BIM',
                'Trasabilitate completa',
                'Conform ISO SR 19650',
              ].map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-1.5 text-xs text-gray-700"
                >
                  <svg
                    className="w-3.5 h-3.5 text-teal-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleButtonClick();
              }}
              disabled={isLoading}
              className={`w-full px-4 py-2.5 rounded-xl text-sm font-bold shadow-md transition ${isLoading ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-700 text-white hover:from-teal-700 hover:via-emerald-700 hover:to-teal-800'}`}
            >
              {isLoading ? (
                <span className="flex items-center gap-2 justify-center">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-white rounded-full animate-spin" />
                  Se trimite...
                </span>
              ) : (
                'Solicita CDE'
              )}
            </button>
          </div>
        </section>

        {authModal}
        {notificationBlock}
      </>
    );
  }

  // Varianta de buton (default)
  return (
    <div className="flex flex-col gap-4">
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleButtonClick();
        }}
        disabled={isLoading}
        className={`relative inline-flex items-center gap-3 px-6 py-3
                   ${
                     isLoading
                       ? 'bg-gray-400 cursor-not-allowed'
                       : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700'
                   }
                   text-white rounded-xl shadow-lg hover:shadow-xl
                   transition-all duration-200 ease-in-out
                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500`}
      >
        <div className="flex items-center justify-center bg-white/20 rounded-lg p-2">
          {isLoading ? (
            <div className="w-[22px] h-[22px] border-2 border-gray-300 border-t-white rounded-full animate-spin" />
          ) : (
            <Database size={22} className="text-white" />
          )}
        </div>

        <div className="flex flex-col text-left leading-tight">
          <span className="font-semibold text-[15px]">
            {isLoading ? 'Se trimite...' : 'Vreau CDE'}
          </span>
          <span className="text-xs text-teal-100">
            {isLoading ? 'Va rugam asteptati' : 'ISO SR 19650'}
          </span>
        </div>
      </button>

      {authModal}
      {notificationBlock}
    </div>
  );
};

export default CDERequestButton;
